/**
 * Florida driver license numbers.
 *
 * One letter and twelve digits, grouped the way the DHSMV prints it on the
 * card: X000-000-00-000-0. Matching the card matters more than looking tidy —
 * somebody checking a number against a license in their hand reads it in the
 * same groups it is printed in.
 *
 * Only Florida. Every other state has its own scheme, and inventing spacing
 * for a Georgia or New York license would make it harder to compare with the
 * card, not easier. Those are left exactly as typed.
 */

/** Digits and the leading letter, nothing else. */
export function stripLicense(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Group a Florida license the way the DHSMV prints it on the card:
 * M323-287-85-460-0 — letter and three, three, two, three, one.
 *
 * Matching the card matters more than any other grouping, because the number
 * is nearly always being copied off one, and a person checking digit by digit
 * should not have to re-count where the groups fall.
 *
 * Formats as far as it can, so the number reads correctly while it is being
 * typed rather than snapping into shape at the end.
 */
export function formatFloridaLicense(raw: string): string {
  const s = stripLicense(raw);
  if (!s) return "";

  // A leading letter is part of the number; without one there is nothing to
  // group by, so leave it be and let validation say so.
  if (!/^[A-Z]/.test(s)) return s;

  const letter = s[0]!;
  const digits = s.slice(1).replace(/\D/g, "").slice(0, 12);

  const parts = [
    letter + digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 8),
    digits.slice(8, 11),
    digits.slice(11, 12),
  ].filter(Boolean);

  return parts.join("-");
}

/** One letter and twelve digits, which is what Florida issues. */
export function isFloridaLicense(raw: string): boolean {
  return /^[A-Z]\d{12}$/.test(stripLicense(raw));
}

/**
 * Complain only when it is definitely wrong.
 *
 * A partly typed number is not an error, and neither is an out-of-state
 * license. This exists to catch a Florida number with a digit missing —
 * which is otherwise invisible until the state rejects the title work.
 */
export function checkFloridaLicense(
  raw: string, state: string, idType = "drivers_license",
): string | null {
  // A Florida passport is not a thing; only licenses and state IDs have this
  // shape, and checking a passport against it rejects valid documents.
  if (idType === "passport" || idType === "military_id") return null;
  if (state.trim().toUpperCase() !== "FL") return null;
  const s = stripLicense(raw);
  if (!s) return null;
  if (!/^[A-Z]/.test(s)) {
    return "A Florida license starts with a letter.";
  }
  if (s.length !== 13) {
    const digits = s.length - 1;
    return `A Florida license is a letter and 12 digits — this has ${digits}.`;
  }
  return null;
}
